import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
 
import {
  getVendorById,
  updateVendor,
  deleteVendor,
  getApiErrorMessage,
} from "../../services/vendorService";
 
import { Vendor, SingleVendorApiResponse } from "../../types/vendor";
 
import EditVendorModal from "../../compoonents/EditVendorModal/EditVendorModal";
import DeleteConfirmationModal from "../../compoonents/DeleteConfirmationModal/DeleteConfirmationModal";
import VendorProductsTab from "../../compoonents/VendorProductsTab/VendorProductsTab";
 
import backArrowIcon from "../../assets/images/previcon.png";
import defaultLogo from "../../assets/images/profile1.png";
import { resolveMediaUrl } from "../../utils/mediaUrl";
 
import "./VendorDetailsPage.css";
 
const VendorDetailsPage: React.FC = () => { 
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
 
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<'details' | 'products'>('details');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
 
  const fetchVendorDetails = useCallback(async () => {
    if (!vendorId) {
      setError("No Vendor ID was provided in the URL.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
       const response: SingleVendorApiResponse = await getVendorById(vendorId);
      
      if (response && response.success) {
        setVendor(response.data);
      } else {
        setError("Could not find the requested vendor.");
      }

    } catch (err) {
      setError("An error occurred while fetching vendor details.");
      console.error("Fetch Vendor Details Error:", err);
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    fetchVendorDetails();
  }, [fetchVendorDetails]);
 
  const handleUpdateVendor = async (updatedData: Partial<Vendor>) => {
    if (!vendorId) return;

    setIsSaving(true);
    try {
      const response = await updateVendor(vendorId, updatedData);
      if (response && response.vendor) {
        setVendor(response.vendor); 
        setIsEditModalOpen(false);
      }
    } catch (err) {
      alert(getApiErrorMessage(err, "Failed to update vendor. Please try again."));
      console.error("Update Vendor Error:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteVendor = async () => {
    if (!vendorId) return;

    setIsDeleting(true);
    try {
      await deleteVendor(vendorId);
      alert("Vendor deleted successfully.");
      navigate("/vendors");
    } catch (err) {
      alert("Failed to delete vendor. Please try again.");
      console.error("Delete Vendor Error:", err);
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };
 
  const renderTabContent = () => {
    if (!vendor) return null;

    switch (activeTab) {
      case 'details':
        return (
          <div className="info-grid">
            <div className="info-pair"><label>Vendor ID</label><span>{vendor.id}</span></div>
            <div className="info-pair"><label>Email</label><span>{vendor.email}</span></div>
            <div className="info-pair"><label>Phone Number</label><span>{vendor.phone}</span></div>
            <div className="info-pair"><label>Date Joined</label><span>{new Date(vendor.createdAt).toLocaleDateString()}</span></div>
            <div className="info-pair full-width"><label>Address</label><span>{vendor.address}</span></div>
            <div className="info-pair full-width"><label>Description</label><span>{vendor.description || 'N/A'}</span></div>
          </div>
        );
      case 'products':
        return <VendorProductsTab vendorId={vendor.id} />;
      default:
        return null;
    }
  };

  if (loading) return <div className="page-loading">Loading Vendor Details...</div>;
  if (error) return <div className="page-error">{error}</div>;
  if (!vendor) return <div className="page-error">Vendor not found.</div>;

  return (
    <>
      <div className="vendor-details-page">
        <div className="details-header">
          <button onClick={() => navigate(-1)} className="back-link-button">
            <img src={backArrowIcon} alt="Back" />
            <span>All Vendors</span>
          </button>
        </div>

        <header className="profile-header">
          <img
            src={resolveMediaUrl(vendor.logo) || defaultLogo}
            alt={`${vendor.name} logo`}
            className="profile-avatar"
            onError={(e) => {
              (e.target as HTMLImageElement).src = defaultLogo;
            }}
          />
          <div className="profile-info">
            <h1>{vendor.name}</h1>
            <span className={`status-badge ${vendor.isActive ? 'status-active' : 'status-inactive'}`}>
              {vendor.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="profile-actions">
            <button className="action-btn-secondary" onClick={() => setIsEditModalOpen(true)}>
              Edit Vendor
            </button>
            <button className="action-btn-danger" onClick={() => setIsDeleteModalOpen(true)}>
              Delete Vendor
            </button>
          </div>
        </header>

        <div className="details-tabs">
          <button
            className={activeTab === 'details' ? 'active' : ''}
            onClick={() => setActiveTab('details')}
          >
            Vendor Information
          </button>
          <button
            className={activeTab === 'products' ? 'active' : ''}
            onClick={() => setActiveTab('products')}
          >
            Products
          </button>
        </div>

        <main className="details-content-container">
            {renderTabContent()}
        </main>
      </div>

      {isEditModalOpen && (
        <EditVendorModal
          vendor={vendor}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleUpdateVendor}
          isSaving={isSaving}
        />
      )}

      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleDeleteVendor}
          isDeleting={isDeleting}
          itemName={vendor.name}
        />
      )}
    </>
  );
};

export default VendorDetailsPage;